import { useCallback } from 'react';
import { AccountResponse } from '../../types/Account.types';
import { Agreements } from '../../types/Agreements.types';
import { useApi } from './useApi';

type journalRequestBody = {
  name: string;
  imprint?: string;
  publishing_model: string;
  issn_e?: string;
  issn_p?: string;
  payment?: string;
  subjects?: string;
  licenses: string[];
  extra_info?: string;
  url?: string;
  disabled?: boolean;
};

export const useAgreements = () => {
  const { makeApiRequest } = useApi();

  const fetchAccounts = useCallback(async () => {
    return await makeApiRequest<AccountResponse>('/accounts', 'GET');
  }, [makeApiRequest]);

  const fetchAgreementById = useCallback(
    async (id: string) => {
      return await makeApiRequest<Agreements>(`/agreements/${id}`, 'GET');
    },
    [makeApiRequest]
  );

  const fetchAgreements = useCallback(async () => {
    return await makeApiRequest<Agreements[]>('/agreements', 'GET');
  }, [makeApiRequest]);

  const fetchLicenses = useCallback(async () => {
    const licenses = await makeApiRequest<string[]>(
      '/settings/licenses',
      'GET'
    );
    return Array.isArray(licenses) ? licenses.sort() : [];
  }, [makeApiRequest]);

  const fetchPayments = useCallback(async () => {
    const payments = await makeApiRequest<string[]>('/settings/payment', 'GET');
    return Array.isArray(payments) ? payments.sort() : payments;
  }, [makeApiRequest]);

  const fetchPublishers = useCallback(async () => {
    return await makeApiRequest<string[]>('/settings/publishers', 'GET');
  }, [makeApiRequest]);

  const fetchPublishingModels = useCallback(async () => {
    const models = await makeApiRequest('/settings/publishing_model', 'GET');
    return Array.isArray(models) ? models.sort() : [];
  }, [makeApiRequest]);

  const addAgreement = async (data: FormData) => {
    return await makeApiRequest<Agreements>('/agreements', 'POST', data);
  };

  const editAgreement = async (data: FormData, id: string) => {
    return await makeApiRequest<Agreements>(`/agreements/${id}`, 'PUT', data);
  };

  const deleteAgreementById = async (id: string) => {
    await makeApiRequest(`/agreements/${id}`, 'DELETE');
  };

  const copyAgreement = async (
    id: string,
    targetAccount: string,
    paymentUrl: string,
    description?: string
  ) => {
    const body: Record<string, string> = {
      target_account: targetAccount,
      payment_url: paymentUrl,
    };
    if (description) body.description = description;
    return await makeApiRequest(`/agreements/${id}/copy`, 'POST', body);
  };

  const previewAgreement = useCallback(
    async (data: FormData) => {
      return await makeApiRequest<Agreements>(
        '/agreements/preview',
        'POST',
        data
      );
    },
    [makeApiRequest]
  );

  const addJournal = async (data: journalRequestBody, agreementId: string) => {
    return await makeApiRequest(
      `/agreements/${agreementId}/journals`,
      'POST',
      data
    );
  };

  const editJournal = async (
    data: journalRequestBody,
    agreementId: string,
    journalId: string
  ) => {
    return await makeApiRequest(
      `/agreements/${agreementId}/journals/${journalId}`,
      'PUT',
      data
    );
  };

  const deleteJournal = async (agreementId: string, journalId: string) => {
    return await makeApiRequest(
      `/agreements/${agreementId}/journals/${journalId}`,
      'DELETE'
    );
  };

  const moveJournal = async (
    agreementId: string,
    journalId: string,
    targetAgreementId: string,
    url: string
  ) => {
    return await makeApiRequest<Agreements>(
      `/agreements/${agreementId}/journals/${journalId}/move`,
      'PUT',
      { target_agreement_id: targetAgreementId, journal_url: url }
    );
  };

  const searchJournals = useCallback(
    async (
      searchQuery: string,
      licenses: string[],
      publishingModels: string[]
    ) => {
      if (searchQuery === '') {
        return [];
      }

      const params = new URLSearchParams();
      params.set('term', searchQuery);
      licenses.forEach((l) => params.append('licenses[]', l));
      publishingModels.forEach((m) => params.append('publishing_model[]', m));
      return await makeApiRequest(`/search?${params.toString()}`, 'GET');
    },
    [makeApiRequest]
  );

  return {
    fetchAccounts,
    copyAgreement,
    fetchAgreementById,
    fetchAgreements,
    fetchLicenses,
    fetchPayments,
    fetchPublishers,
    fetchPublishingModels,
    addAgreement,
    deleteAgreementById,
    editAgreement,
    previewAgreement,
    addJournal,
    editJournal,
    deleteJournal,
    moveJournal,
    searchJournals,
  };
};
